import React, { useState, useCallback } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SkyCatch from '../components/games/SkyCatch';
import { PastScoreLookup } from '../components/games/PastScoreLookup';
import { Trophy, Mail, User, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from '../hooks/use-toast';
import { publicApi } from '../api';

const GamePage = () => {
  const [best, setBest] = useState(0);
  const [lastScore, setLastScore] = useState(0);
  const [showEntry, setShowEntry] = useState(false);
  const [entry, setEntry] = useState({ name: '', email: '', city: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleFinish = useCallback((val) => {
    setLastScore(val);
    setBest((b) => Math.max(b, val));
    setTimeout(() => setShowEntry(true), 400);
  }, []);

  const closeEntry = useCallback(() => setShowEntry(false), []);

  const submitEntry = async (e) => {
    e.preventDefault();
    if (!entry.name || !entry.email || !entry.city) return;
    setSubmitting(true);
    try {
      const res = await publicApi.submitEntry({ ...entry, score: lastScore });
      localStorage.setItem('skycatch_name', entry.name.trim().slice(0, 20));
      setShowEntry(false);
      const msg = res.updated
        ? `New best: ${res.best}. You climbed the leaderboard!`
        : res.created
          ? `Locked in at ${res.best}. Check the leaderboard!`
          : `Kept your best score of ${res.best}.`;
      toast({ title: 'Entry submitted!', description: msg });
      setEntry({ name: '', email: '', city: '' });
    } catch (err) {
      toast({ title: 'Submission failed', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#FFF4E6] min-h-screen">
      <Navbar />
      <section className="px-4 md:px-8 pt-24 md:pt-28 pb-16">
        <div className="max-w-3xl mx-auto">
          <div className="text-xs font-bold uppercase tracking-widest text-[#E63946]">Crunch Contest Game</div>
          <h1 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] mt-2">
            Sky <span className="italic">Catch.</span>
          </h1>
          <p className="font-brico text-base md:text-lg mt-3 max-w-lg opacity-70">
            30 seconds. Catch the orbs, dodge the bombs, build your combo. Top 5 win a real TruCrunch hamper.
          </p>

          <div className="mt-3 flex items-center gap-3">
            <div className="inline-flex items-center gap-2 bg-white rounded-full px-3 py-1.5 crunch-shadow-sm text-xs font-bold uppercase tracking-wider">
              <Trophy size={14} className="text-[#E63946]" /> Best: {best}
            </div>
          </div>

          <div className="mt-6">
            <SkyCatch onFinish={handleFinish} />
          </div>

          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/leaderboard" className="btn-chunky outline"><Trophy size={16} /> View Leaderboard</Link>
          </div>

          <PastScoreLookup />
        </div>
      </section>

      {showEntry && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={closeEntry}>
          <form data-testid="contest-entry-modal" onClick={(e) => e.stopPropagation()} onSubmit={submitEntry} className="bg-[#FFF4E6] rounded-3xl crunch-shadow p-6 md:p-8 max-w-md w-full">
            <div className="text-xs font-bold uppercase tracking-widest text-[#E63946]">Enter Contest</div>
            <h3 className="font-display text-3xl md:text-4xl uppercase mt-1">Save your score!</h3>
            <p className="font-brico mt-2 opacity-70">Your score: <span className="font-bold">{lastScore}</span>. Top 5 win a real TruCrunch hamper worth ₹999.</p>
            <div className="mt-5 space-y-3">
              <div className="flex items-center gap-2 bg-white rounded-2xl border-2 border-[#1a1a1a] px-4">
                <User size={16} />
                <input data-testid="entry-name-input" required value={entry.name} onChange={(e) => setEntry({ ...entry, name: e.target.value })} placeholder="Your name" className="flex-1 py-3 bg-transparent focus:outline-none font-brico" />
              </div>
              <div className="flex items-center gap-2 bg-white rounded-2xl border-2 border-[#1a1a1a] px-4">
                <Mail size={16} />
                <input data-testid="entry-email-input" required type="email" value={entry.email} onChange={(e) => setEntry({ ...entry, email: e.target.value })} placeholder="Email" className="flex-1 py-3 bg-transparent focus:outline-none font-brico" />
              </div>
              <div className="flex items-center gap-2 bg-white rounded-2xl border-2 border-[#1a1a1a] px-4">
                <MapPin size={16} />
                <input data-testid="entry-city-input" required value={entry.city} onChange={(e) => setEntry({ ...entry, city: e.target.value })} placeholder="City" className="flex-1 py-3 bg-transparent focus:outline-none font-brico" />
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button data-testid="entry-skip-btn" type="button" onClick={closeEntry} className="btn-chunky outline flex-1 justify-center">Skip</button>
              <button data-testid="entry-submit-btn" type="submit" disabled={submitting} className="btn-chunky primary flex-1 justify-center">{submitting ? 'Submitting…' : 'Submit'}</button>
            </div>
          </form>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default GamePage;
